import { Controller } from "@hotwired/stimulus"

const WORKER_CODE = `
self.onmessage = function(e) {
  var url = e.data.url;
  var currentCount = e.data.currentCount;
  var interval = e.data.interval || 4000;
  var delayThreshold = e.data.delayThreshold || 4;
  var maxAttempts = e.data.maxAttempts || 30;
  var attempts = 0;
  var delayNotified = false;

  function runCheck() {
    setTimeout(function() {
      attempts++;
      fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include'
      })
      .then(function(res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function(data) {
        var newCount = typeof data.count === 'number' ? data.count : (data.candidate_job_ids ? data.candidate_job_ids.length : 0);
        if (newCount !== currentCount) {
          self.postMessage({ status: 'changed', data: data, attempts: attempts });
          return;
        }

        if (data.event_status === 'failed') {
          self.postMessage({ status: 'failed', data: data, attempts: attempts });
          return;
        }

        if (data.event_status === 'completed') {
          if (newCount > 0) {
            self.postMessage({ status: 'changed', data: data, attempts: attempts });
          } else {
            self.postMessage({ status: 'empty', data: data, attempts: attempts });
          }
          return;
        }

        if (attempts >= delayThreshold && !delayNotified) {
          delayNotified = true;
          self.postMessage({ status: 'delayed', data: data, attempts: attempts });
        }

        if (attempts < maxAttempts) {
          runCheck();
          return;
        }

        self.postMessage({ status: 'timeout', data: data, attempts: attempts });
      })
      .catch(function(err) {
        // If worker fetch encounters an error, report worker_error so main thread can execute fallback
        self.postMessage({ status: 'worker_error', error: (err && err.message) || 'Worker fetch failed', attempts: attempts });
      });
    }, interval);
  }

  runCheck();
};
`;

export default class MatchmakingController extends Controller {
  static targets = ["count", "buttonContainer", "cardsContainer", "bulkActionContainer", "selectAllContainer"]
  static values = {
    jobId: Number,
    fetchUrl: String,
    initialCount: { type: Number, default: 0 },
    interval: { type: Number, default: 4000 },
    delayThreshold: { type: Number, default: 4 },
    maxAttempts: { type: Number, default: 30 },
    isPending: { type: Boolean, default: false },
    successText: { type: String, default: "Lista atualizada" },
    emptyText: { type: String, default: "Não encontramos ninguém dessa vez 🙁" },
    errorText: { type: String, default: "Erro ao gerar lista" },
    delayStep1Text: { type: String, default: "Está demorando mais do que normal..." },
    delayStep2Text: { type: String, default: "Mas vamos atualizar daqui a pouco" },
    timeoutText: { type: String, default: "Tempo limite excedido ao gerar lista" },
    closeText: { type: String, default: "Fechar" }
  }

  connect() {
    this.currentCount = this.readCurrentCount();
    this.worker = null;
    this.fallbackTimer = null;
    this.isPolling = false;

    const buttonContainer = this.findButtonContainer();
    if (buttonContainer && !this.lastButtonHtml) {
      this.lastButtonHtml = buttonContainer.innerHTML;
    }

    if (this.isPendingValue) {
      this.startPolling();
    }
  }

  disconnect() {
    this.stopWorker();
  }

  onRequestSubmitted(event) {
    if (event) {
      const form = event.target;
      const input = form.querySelector ? form.querySelector('input[name="max_distance_km"]') : null;
      if (input) {
        let val = parseFloat(input.value);
        if (isNaN(val) || val < 10) {
          val = 10;
          input.value = "10";
        } else if (val > 100) {
          val = 100;
          input.value = "100";
        }
      }

      const modalEl = (form.closest && form.closest('.modal')) || document.getElementById(`request-matchmaking-modal-${this.jobIdValue}`);
      if (modalEl && typeof bootstrap !== "undefined" && bootstrap.Modal) {
        const modalInstance = bootstrap.Modal.getInstance(modalEl) || bootstrap.Modal.getOrCreateInstance(modalEl);
        if (modalInstance) {
          modalInstance.hide();
        }
      }
    }

    this.currentCount = this.readCurrentCount();
    const btnContainer = this.findButtonContainer();
    if (btnContainer) {
      btnContainer.innerHTML = `
        <button type="button" class="btn btn-sm btn-outline-secondary w-100" disabled>
          <i class="fa-solid fa-arrows-rotate fa-spin me-1"></i> Aguardando lista
        </button>
      `;
    }

    this.stopWorker();
    setTimeout(() => {
      this.startPolling();
    }, 100);
  }

  resolveAbsoluteUrl(pathOrUrl) {
    if (!pathOrUrl) return "";
    try {
      return new URL(pathOrUrl, window.location.origin).href;
    } catch (_) {
      return pathOrUrl;
    }
  }

  startPolling() {
    if (this.isPolling) return;
    this.isPolling = true;

    this.stopWorker();
    const targetUrl = this.resolveAbsoluteUrl(this.fetchUrlValue);

    this.worker = this.createWorker();
    this.worker.onmessage = (event) => this.handleWorkerMessage(event.data);

    this.worker.postMessage({
      url: targetUrl,
      currentCount: this.currentCount,
      interval: this.intervalValue,
      delayThreshold: this.delayThresholdValue,
      maxAttempts: this.maxAttemptsValue
    });
  }

  handleWorkerMessage(message) {
    if (!message) return;

    if (message.status === "worker_error") {
      console.warn("[Matchmaking] Worker fetch failed, switching to main-thread polling fallback:", message.error);
      this.stopWorker();
      this.isPolling = true;
      this.runMainThreadPolling(message.attempts || 1);
      return;
    }

    if (message.status === "delayed") {
      this.onListDelayed(message.data);
      return;
    }

    this.isPolling = false;
    this.stopWorker();

    if (message.status === "changed") {
      this.onListChanged(message.data);
      return;
    }

    if (message.status === "failed") {
      this.onListFailed(message.data);
      return;
    }

    if (message.status === "timeout") {
      this.onListTimeout(message.data);
      return;
    }

    this.onListEmpty(message.data);
  }

  runMainThreadPolling(startAttempt = 1) {
    let attempts = startAttempt;
    let delayNotified = false;
    const targetUrl = this.resolveAbsoluteUrl(this.fetchUrlValue);
    const interval = this.intervalValue || 4000;
    const delayThreshold = this.delayThresholdValue || 4;
    const maxAttempts = this.maxAttemptsValue || 30;

    const executeCheck = () => {
      this.fallbackTimer = setTimeout(() => {
        attempts++;
        fetch(targetUrl, {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          credentials: "include"
        })
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
          })
          .then((data) => {
            const newCount = typeof data.count === "number" ? data.count : (data.candidate_job_ids ? data.candidate_job_ids.length : 0);
            if (newCount !== this.currentCount) {
              this.isPolling = false;
              this.onListChanged(data);
              return;
            }

            if (data.event_status === "failed") {
              this.isPolling = false;
              this.onListFailed(data);
              return;
            }

            if (data.event_status === "completed") {
              this.isPolling = false;
              if (newCount > 0) {
                this.onListChanged(data);
              } else {
                this.onListEmpty(data);
              }
              return;
            }

            if (attempts >= delayThreshold && !delayNotified) {
              delayNotified = true;
              this.onListDelayed(data);
            }

            if (attempts < maxAttempts) {
              this.isPolling = true;
              executeCheck();
              return;
            }

            this.isPolling = false;
            this.onListTimeout(data);
          })
          .catch((err) => {
            console.error("[Matchmaking] Main thread fetch failed:", err);
            if (attempts < maxAttempts) {
              this.isPolling = true;
              executeCheck();
              return;
            }

            this.isPolling = false;
            this.onListFailed(null);
          });
      }, interval);
    };

    executeCheck();
  }

  onListChanged(data) {
    if (!data) return;

    this.currentCount = typeof data.count === "number" ? data.count : this.currentCount;
    if (data.button_html) {
      this.lastButtonHtml = data.button_html;
    }

    const countEl = this.findCountElement();
    if (countEl) countEl.textContent = this.currentCount;

    const cardsEl = this.findCardsContainer();
    if (cardsEl && data.cards_html) {
      cardsEl.innerHTML = data.cards_html;
    }

    const selectAllEl = this.findSelectAllContainer();
    if (selectAllEl && typeof data.select_all_html !== "undefined") {
      selectAllEl.innerHTML = data.select_all_html;
    }

    const bulkEl = this.findBulkActionContainer();
    if (bulkEl && typeof data.bulk_action_html !== "undefined") {
      bulkEl.innerHTML = data.bulk_action_html;
    }

    const btnContainer = this.findButtonContainer();
    if (btnContainer) {
      btnContainer.innerHTML = data.success_tag_html || this.fallbackSuccessTagHtml();
    }

    this.refreshTooltips();
  }

  onListEmpty(data) {
    if (data && data.button_html) {
      this.lastButtonHtml = data.button_html;
    }

    const btnContainer = this.findButtonContainer();
    if (btnContainer) {
      btnContainer.innerHTML = (data && data.empty_tag_html) || this.fallbackEmptyTagHtml();
    }
  }

  onListFailed(data) {
    if (data && data.button_html) {
      this.lastButtonHtml = data.button_html;
    }

    const btnContainer = this.findButtonContainer();
    if (btnContainer) {
      btnContainer.innerHTML = (data && data.error_tag_html) || this.fallbackErrorTagHtml();
    }
  }

  onListDelayed(data) {
    if (data && data.button_html) {
      this.lastButtonHtml = data.button_html;
    }

    const btnContainer = this.findButtonContainer();
    if (btnContainer) {
      btnContainer.innerHTML = (data && data.timeout_tag_html) || this.fallbackDelayedTagHtml();
    }
  }

  onListTimeout(data) {
    if (data && data.button_html) {
      this.lastButtonHtml = data.button_html;
    }

    const btnContainer = this.findButtonContainer();
    if (btnContainer) {
      btnContainer.innerHTML = (data && data.timeout_tag_html) || this.fallbackDelayedTagHtml();
    }
  }

  closeTag(event) {
    if (event) event.preventDefault();

    const btnContainer = this.findButtonContainer();
    if (!btnContainer) return;

    if (this.currentCount <= 5 && this.lastButtonHtml) {
      btnContainer.innerHTML = this.lastButtonHtml;
      return;
    }

    btnContainer.innerHTML = "";
  }

  createWorker() {
    if (typeof window !== "undefined" && typeof window.Worker !== "undefined") {
      try {
        const blob = new Blob([WORKER_CODE], { type: "application/javascript" });
        const workerUrl = URL.createObjectURL(blob);
        const worker = new Worker(workerUrl);
        return {
          postMessage: (msg) => worker.postMessage(msg),
          terminate: () => {
            try {
              worker.terminate();
              URL.revokeObjectURL(workerUrl);
            } catch (_) {}
          },
          set onmessage(fn) {
            worker.onmessage = fn;
          }
        };
      } catch (err) {
        console.warn("[Matchmaking] Web Worker creation failed, using main-thread fallback", err);
      }
    }

    return this.createFallbackWorker();
  }

  createFallbackWorker() {
    let timerId = null;
    let terminated = false;
    let handler = null;

    return {
      postMessage: ({ url, currentCount, interval, delayThreshold, maxAttempts }) => {
        let attempts = 0;
        let delayNotified = false;
        const pollInterval = interval || 4000;
        const threshold = delayThreshold || 4;
        const max = maxAttempts || 30;

        const check = () => {
          if (terminated) return;
          timerId = setTimeout(() => {
            if (terminated) return;
            attempts++;
            fetch(url, {
              headers: {
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest"
              },
              credentials: "include"
            })
              .then((res) => {
                if (!res.ok) throw new Error("HTTP " + res.status);
                return res.json();
              })
              .then((data) => {
                if (terminated) return;
                const newCount = typeof data.count === "number" ? data.count : (data.candidate_job_ids ? data.candidate_job_ids.length : 0);
                if (newCount !== currentCount) {
                  if (handler) handler({ data: { status: "changed", data, attempts } });
                  return;
                }

                if (data.event_status === "failed") {
                  if (handler) handler({ data: { status: "failed", data, attempts } });
                  return;
                }

                if (data.event_status === "completed") {
                  if (handler) {
                    if (newCount > 0) {
                      handler({ data: { status: "changed", data, attempts } });
                    } else {
                      handler({ data: { status: "empty", data, attempts } });
                    }
                  }
                  return;
                }

                if (attempts >= threshold && !delayNotified) {
                  delayNotified = true;
                  if (handler) handler({ data: { status: "delayed", data, attempts } });
                }

                if (attempts < max) {
                  check();
                  return;
                }

                if (handler) handler({ data: { status: "timeout", data, attempts } });
              })
              .catch((err) => {
                if (terminated) return;
                if (attempts < max) {
                  check();
                  return;
                }

                if (handler) handler({ data: { status: "failed", error: err.message, attempts } });
              });
          }, pollInterval);
        };

        check();
      },
      terminate: () => {
        terminated = true;
        if (timerId) clearTimeout(timerId);
      },
      set onmessage(fn) {
        handler = fn;
      }
    };
  }

  stopWorker() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
    this.isPolling = false;
  }

  readCurrentCount() {
    const countEl = this.findCountElement();
    if (!countEl) return this.initialCountValue || 0;
    const parsed = parseInt(countEl.textContent.trim(), 10);
    return isNaN(parsed) ? (this.initialCountValue || 0) : parsed;
  }

  findCountElement() {
    if (this.hasCountTarget) return this.countTarget;
    return document.getElementById(`matchmaking-count-${this.jobIdValue}`);
  }

  findButtonContainer() {
    if (this.hasButtonContainerTarget) return this.buttonContainerTarget;
    return document.getElementById(`matchmaking-request-list-${this.jobIdValue}`);
  }

  findCardsContainer() {
    if (this.hasCardsContainerTarget) return this.cardsContainerTarget;
    return document.getElementById("matchmaking-cards-container");
  }

  findBulkActionContainer() {
    if (this.hasBulkActionContainerTarget) return this.bulkActionContainerTarget;
    return document.getElementById(`matchmaking-bulk-action-${this.jobIdValue}`);
  }

  findSelectAllContainer() {
    if (this.hasSelectAllContainerTarget) return this.selectAllContainerTarget;
    return document.getElementById(`matchmaking-select-all-container-${this.jobIdValue}`);
  }

  refreshTooltips() {
    if (typeof bootstrap === "undefined" || !bootstrap.Tooltip) return;
    const tooltips = this.element.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltips.forEach((el) => bootstrap.Tooltip.getOrCreateInstance(el));
  }

  fallbackSuccessTagHtml() {
    const text = this.successTextValue || "Lista atualizada";
    const closeTitle = this.closeTextValue || "Fechar";
    return `
      <div class="alert alert-success d-flex align-items-center justify-content-between p-2 mb-0 border-0" role="alert" style="border-radius: 6px; font-size: 0.85rem; background-color: #d1fae5; color: #065f46; word-break: break-word;">
        <div class="d-flex align-items-center me-2">
          <i class="fa-solid fa-circle-check me-2 text-success fs-6 flex-shrink-0"></i>
          <span class="fw-semibold lh-sm">${text}</span>
        </div>
        <button type="button" class="btn-close ms-2 flex-shrink-0" style="font-size: 0.65rem; padding: 0.25rem;" aria-label="${closeTitle}" data-action="click->matchmaking#closeTag"></button>
      </div>
    `;
  }

  fallbackEmptyTagHtml() {
    const text = this.emptyTextValue || "Não encontramos ninguém dessa vez 🙁";
    const closeTitle = this.closeTextValue || "Fechar";
    return `
      <div class="alert alert-warning d-flex align-items-center justify-content-between p-2 mb-0 border-0" role="alert" style="border-radius: 6px; font-size: 0.85rem; background-color: #fef3c7; color: #92400e; word-break: break-word;">
        <div class="d-flex align-items-center me-2">
          <i class="fa-regular fa-face-frown me-2 fs-6 flex-shrink-0" style="color: #d97706;"></i>
          <span class="fw-semibold lh-sm">${text}</span>
        </div>
        <button type="button" class="btn-close ms-2 flex-shrink-0" style="font-size: 0.65rem; padding: 0.25rem;" aria-label="${closeTitle}" data-action="click->matchmaking#closeTag"></button>
      </div>
    `;
  }

  fallbackErrorTagHtml() {
    const text = this.errorTextValue || "Erro ao gerar lista";
    const closeTitle = this.closeTextValue || "Fechar";
    return `
      <div class="alert alert-danger d-flex align-items-center justify-content-between p-2 mb-0 border-0" role="alert" style="border-radius: 6px; font-size: 0.85rem; background-color: #fee2e2; color: #991b1b; word-break: break-word;">
        <div class="d-flex align-items-center me-2">
          <i class="fa-solid fa-circle-exclamation me-2 text-danger fs-6 flex-shrink-0"></i>
          <span class="fw-semibold lh-sm">${text}</span>
        </div>
        <button type="button" class="btn-close ms-2 flex-shrink-0" style="font-size: 0.65rem; padding: 0.25rem;" aria-label="${closeTitle}" data-action="click->matchmaking#closeTag"></button>
      </div>
    `;
  }

  fallbackDelayedTagHtml() {
    const text1 = this.delayStep1TextValue || "Está demorando mais do que normal...";
    const text2 = this.delayStep2TextValue || "Mas vamos atualizar daqui a pouco";
    const closeTitle = this.closeTextValue || "Fechar";
    return `
      <div class="alert alert-warning d-flex align-items-center justify-content-between p-2 mb-0 border-0 matchmaking-delay-tag" role="alert" style="border-radius: 6px; font-size: 0.85rem; background-color: #ffedd5; color: #9a3412; word-break: break-word;">
        <div class="d-flex align-items-center me-2 overflow-hidden flex-grow-1" style="min-height: 22px;">
          <i class="fa-solid fa-clock me-2 fs-6 flex-shrink-0" style="color: #ea580c;"></i>
          <div class="matchmaking-delay-text-container position-relative flex-grow-1" style="min-height: 20px;">
            <span class="matchmaking-delay-text matchmaking-delay-text-1 fw-semibold lh-sm">${text1}</span>
            <span class="matchmaking-delay-text matchmaking-delay-text-2 fw-semibold lh-sm">${text2}</span>
          </div>
        </div>
        <button type="button" class="btn-close ms-2 flex-shrink-0" style="font-size: 0.65rem; padding: 0.25rem;" aria-label="${closeTitle}" data-action="click->matchmaking#closeTag"></button>
      </div>
      <style>
        .matchmaking-delay-tag .matchmaking-delay-text-container { display: flex; align-items: center; }
        .matchmaking-delay-tag .matchmaking-delay-text-1 { display: block; animation: matchmakingFadeStep1 7s infinite ease-in-out; }
        .matchmaking-delay-tag .matchmaking-delay-text-2 { display: block; position: absolute; top: 0; left: 0; right: 0; animation: matchmakingFadeStep2 7s infinite ease-in-out; }
        @keyframes matchmakingFadeStep1 { 0%, 45% { opacity: 1; visibility: visible; transform: translateY(0); } 50%, 95% { opacity: 0; visibility: hidden; transform: translateY(-4px); } 100% { opacity: 1; visibility: visible; transform: translateY(0); } }
        @keyframes matchmakingFadeStep2 { 0%, 45% { opacity: 0; visibility: hidden; transform: translateY(4px); } 50%, 95% { opacity: 1; visibility: visible; transform: translateY(0); } 100% { opacity: 0; visibility: hidden; transform: translateY(4px); } }
      </style>
    `;
  }

  fallbackTimeoutTagHtml() {
    return this.fallbackDelayedTagHtml();
  }
}
